import { CALCULATION_PROCESS_END, CALCULATION_PROCESS_START } from 'actions/designer'
import { DATA_PROVIDER_ERROR } from 'actions/restActions'
import { useActionsListener } from 'ducks/action_listener'
import { GENERATE_DOCUMENT, GENERATE_DOCUMENT_FAILURE, GENERATE_DOCUMENT_SUCCESS } from 'ducks/generateDocument'
import { checkIfCalculationsBlockByShading } from 'projectSections/sections/proposal/utils'
import { useRef } from 'react'
import { useErrorHandling, useLongProcessErrorHandler } from 'react-admin'

export const useAdditionalErrorHandling = () => {
  const errorHandling = useErrorHandling()
  const longError = useLongProcessErrorHandler()
  const currentLongErrors = useRef<Record<string, () => void>>({})
  const SYNC_SYSTEM_CALC_TIMEOUT_MS = 30 * 1000

  const startLongError = (key: string, timeout: number, error: Error) => {
    if (currentLongErrors.current[key]) {
      console.log('Overlapping long error requests, something seems wrong: ', key)
      return
    }
    currentLongErrors.current[key] = longError.core(timeout, error).complete
  }

  const completeLongError = (key: string) => {
    const completeFunc = currentLongErrors.current[key]
    if (!completeFunc) return
    completeFunc()
    delete currentLongErrors.current[key]
  }

  // Hook into redux
  useActionsListener((payload) => {
    switch (payload.type) {
      case DATA_PROVIDER_ERROR:
        // As this is part of a reducer, this must be delayed or it won't actually show the notification
        setTimeout(() => errorHandling.data_provider(payload.error, payload.opts), 0)
        break
      case CALCULATION_PROCESS_START: {
        if (!payload.payload.forceShadingCalculation) {
          let system = window.editor.objectByUuid(payload.payload.uuid)
          if (checkIfCalculationsBlockByShading(system, true)) {
            // TODO: remove this once calcs flow has been simplified
            // If the system is larger than 50 panels, the process will will remain
            // pending until explicitly triggered, so doesn't make sense to report this as an error
            return
          }
        }

        const timeout =
          window.SceneHelper.systemCalcRequestConfig(payload.payload.uuid).longProcessTimeoutMs ??
          SYNC_SYSTEM_CALC_TIMEOUT_MS

        startLongError(
          'system-calcs_' + payload.payload.uuid,
          timeout,
          new Error(`System calcs incomplete after ${Math.floor(timeout / 1000)}s`)
        )
        break
      }
      case CALCULATION_PROCESS_END: {
        completeLongError('system-calcs_' + payload.payload.uuid)
        break
      }
      case GENERATE_DOCUMENT: {
        startLongError('doc-gen_' + payload.payload.url, 30000, new Error('Document generation incomplete after 30s'))
        break
      }
      case GENERATE_DOCUMENT_FAILURE:
      case GENERATE_DOCUMENT_SUCCESS: {
        completeLongError('doc-gen_' + payload.requestPayload.url)
        break
      }
    }
  })
}
